import { Link, usePage } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

type Country = {
  code: string
  name: string
  status: 'active' | 'scaffolded'
  description: string
}

type Props = {
  countries: Country[]
  us_case_counts: {
    total: number
    draft: number
    ready: number
    paid: number
    generated: number
    filed: number
  }
}

export default function BusinessRegistrationDashboard({ countries, us_case_counts }: Props) {
  const page = usePage()
  const accountSlug = (page.props as { auth?: { account?: { slug?: string } } }).auth?.account?.slug

  return (
    <DashboardLayout title="Business Registration">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Registration Hub</CardTitle>
            <CardDescription>
              Manage multi-jurisdiction business registration and filing workflows.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
              <Stat label="Total" value={us_case_counts.total} />
              <Stat label="Draft" value={us_case_counts.draft} />
              <Stat label="Ready" value={us_case_counts.ready} />
              <Stat label="Paid" value={us_case_counts.paid} />
              <Stat label="Generated" value={us_case_counts.generated} />
              <Stat label="Filed" value={us_case_counts.filed} />
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-4">
          {countries.map((country) => (
            <Card key={country.code}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{country.name}</CardTitle>
                  <Badge variant={country.status === 'active' ? 'default' : 'outline'}>
                    {country.status}
                  </Badge>
                </div>
                <CardDescription>{country.description}</CardDescription>
              </CardHeader>
              <CardContent>
                {country.code === 'US' ? (
                  <Link className="underline text-sm" href={`/${accountSlug}/business-registration/us/cases`}>
                    Open US registrations
                  </Link>
                ) : (
                  <Link className="underline text-sm" href={`/${accountSlug}/business-registration/uk/dashboard`}>
                    Open UK scaffold
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="border rounded-lg p-3">
      <p className="text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  )
}
